import React, { useState, useEffect } from 'react';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Settings,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  GripVertical,
  Save,
  RotateCcw,
  Columns,
  AlertCircle,
  List,
  FileText,
  ChevronRight,
  Layers,
  Check,
  X
} from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const SECTION_INFO = {
  alerts: { label: 'Alertas', icon: '⚠️', color: '#ef4444' },
  movimientos: { label: 'Movimientos', icon: '💰', color: '#3b82f6' },
  padron: { label: 'Padrón de Clientes', icon: '👤', color: '#8b5cf6' },
  cases: { label: 'Casos', icon: '📋', color: '#22c55e' },
  monitors: { label: 'Monitores', icon: '📊', color: '#f97316' },
  users: { label: 'Usuarios', icon: '👥', color: '#64748b' }
};

const getSectionInfo = (section) => {
  if (SECTION_INFO[section.key]) {
    return SECTION_INFO[section.key];
  }
  // Para secciones de MySQL
  if (section.origen === 'mysql') {
    return {
      label: section.nombre || section.tabla,
      icon: '🗄️',
      color: '#059669'
    };
  }
  return { label: section.key, icon: '📄', color: '#6b7280' };
};

const VIEW_INFO = {
  lista: { label: 'Vista de Lista', icon: List, description: 'Columnas visibles en la tabla principal' },
  detalle: { label: 'Vista de Detalle', icon: FileText, description: 'Campos visibles en la vista detallada' }
};

const FieldConfigPage = () => {
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState('');
  const [selectedView, setSelectedView] = useState('lista');
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [availableViews, setAvailableViews] = useState(['lista']);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadSections();
  }, []);

  useEffect(() => {
    if (selectedSection) {
      const sectionData = sections.find(s => s.key === selectedSection);
      if (sectionData) {
        setAvailableViews(sectionData.vistas || ['lista']);
        if (!sectionData.vistas?.includes(selectedView)) {
          setSelectedView('lista');
        }
      }
      loadSectionConfig(selectedSection, selectedView);
    }
  }, [selectedSection]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedSection && selectedView) {
      loadSectionConfig(selectedSection, selectedView);
    }
  }, [selectedView]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSections = async () => {
    try {
      // Cargar solo secciones del sistema (sin MySQL que no se usan)
      const sectionsRes = await api.get('/field-config/sections');
      
      const allSections = sectionsRes.data.map(s => ({ ...s, origen: 'sistema' }));
      
      setSections(allSections);
      if (allSections.length > 0) {
        setSelectedSection(allSections[0].key);
        setAvailableViews(allSections[0].vistas || ['lista']);
      }
    } catch (error) {
      toast.error('Error al cargar secciones');
    } finally {
      setLoading(false);
    }
  };

  const loadSectionConfig = async (section, view) => {
    try {
      // Si es una sección de MySQL, cargar los campos directamente
      const sectionData = sections.find(s => s.key === section);
      
      if (sectionData?.origen === 'mysql') {
        // Para MySQL, usar los campos de la sección si ya los tenemos
        // o cargar desde la tabla
        const existingConfig = await api.get(`/field-config/section/${section}?vista=${view}`)
          .catch(() => null);
        
        if (existingConfig?.data?.campos?.length > 0) {
          setConfig(existingConfig.data);
        } else {
          // Usar los campos que ya vienen con la sección
          setConfig({
            seccion: section,
            vista: view,
            campos: sectionData.campos || [],
            origen: 'mysql',
            tabla: sectionData.tabla
          });
        }
      } else {
        const res = await api.get(`/field-config/section/${section}?vista=${view}`);
        setConfig(res.data);
      }
      setHasChanges(false);
    } catch (error) {
      toast.error('Error al cargar configuración');
    }
  };

  const handleToggleVisible = (index) => {
    const newCampos = [...config.campos];
    if (newCampos[index].bloqueado) {
      toast.error('Este campo está bloqueado y no puede ocultarse');
      return;
    }
    newCampos[index].visible = !newCampos[index].visible;
    setConfig({ ...config, campos: newCampos });
    setHasChanges(true);
  };

  const handleToggleLocked = (index) => {
    const newCampos = [...config.campos];
    newCampos[index].bloqueado = !newCampos[index].bloqueado;
    if (newCampos[index].bloqueado) {
      newCampos[index].visible = true;
    }
    setConfig({ ...config, campos: newCampos });
    setHasChanges(true);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    
    const newCampos = Array.from(config.campos);
    const [reorderedItem] = newCampos.splice(result.source.index, 1);
    newCampos.splice(result.destination.index, 0, reorderedItem);
    
    newCampos.forEach((campo, idx) => {
      campo.orden = idx + 1;
    });
    
    setConfig({ ...config, campos: newCampos });
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Determinar si es una sección de MySQL
      const sectionData = sections.find(s => s.key === selectedSection);
      
      if (sectionData?.origen === 'mysql') {
        await api.put(`/field-config/mysql/table/${sectionData.tabla}`, config.campos);
      } else {
        await api.put(`/field-config/section/${selectedSection}?vista=${selectedView}`, {
          campos: config.campos
        });
      }
      toast.success('Configuración guardada');
      setHasChanges(false);
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      await api.post(`/field-config/section/${selectedSection}/reset?vista=${selectedView}`);
      toast.success('Configuración restablecida');
      await loadSectionConfig(selectedSection, selectedView);
      setShowResetDialog(false);
    } catch (error) {
      toast.error('Error al restablecer');
    }
  };

  const visibleCount = config?.campos?.filter(c => c.visible).length || 0;
  const totalCount = config?.campos?.length || 0;

  const filteredCampos = config?.campos?.filter(campo => {
    if (!searchTerm) return true;
    const nombre = (campo.nombre || campo.campo || '').toLowerCase();
    const label = (campo.label || '').toLowerCase();
    const search = searchTerm.toLowerCase();
    return nombre.includes(search) || label.includes(search);
  }) || [];

  const sectionInfo = SECTION_INFO[selectedSection] || { label: selectedSection, icon: '📄', color: '#64748b' };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <Settings className="animate-spin text-purple-600" size={40} />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Columns className="text-purple-600" size={28} />
          Configuración de Campos
        </h1>
        <p className="text-slate-600 mt-1">
          Personaliza qué campos se muestran en cada sección y su orden
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar - Selección de Módulo */}
        <div className="lg:col-span-1 space-y-4">
          <Card className="p-4 bg-white">
            <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
              <Layers size={16} className="text-purple-600" />
              Módulo
            </h3>
            <div className="space-y-2">
              {sections.map(section => {
                const info = getSectionInfo(section);
                const isSelected = selectedSection === section.key;
                
                return (
                  <button
                    key={section.key}
                    onClick={() => setSelectedSection(section.key)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all ${
                      isSelected 
                        ? 'bg-purple-50 border-2 border-purple-300' 
                        : 'bg-slate-50 border-2 border-transparent hover:border-slate-200'
                    }`}
                  >
                    <span className="text-xl">{info.icon}</span>
                    <div className="flex-1 text-left">
                      <p className={`font-medium text-sm ${isSelected ? 'text-purple-700' : 'text-slate-700'}`}>
                        {info.label}
                      </p>
                      <p className="text-xs text-slate-500">
                        {section.campos_count || section.totalCampos || 0} campos
                      </p>
                    </div>
                    {isSelected && <ChevronRight size={16} className="text-purple-500" />}
                  </button>
                );
              })}
            </div>
          </Card>

          {/* Selector de Vista */}
          <Card className="p-4 bg-white">
            <h3 className="font-semibold text-sm mb-3">Vista</h3>
            <div className="space-y-2">
              {availableViews.map(view => {
                const info = VIEW_INFO[view] || { label: view, icon: List };
                const ViewIcon = info.icon;
                const isSelected = selectedView === view;
                
                return (
                  <button
                    key={view}
                    onClick={() => setSelectedView(view)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all ${
                      isSelected 
                        ? 'bg-blue-50 border-2 border-blue-300' 
                        : 'bg-slate-50 border-2 border-transparent hover:border-slate-200'
                    }`}
                  >
                    <ViewIcon size={18} className={isSelected ? 'text-blue-600' : 'text-slate-500'} />
                    <div className="flex-1 text-left">
                      <p className={`font-medium text-sm ${isSelected ? 'text-blue-700' : 'text-slate-700'}`}>
                        {info.label}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </Card>

          {/* Resumen */}
          <Card className="p-4 bg-white">
            <h3 className="font-semibold text-sm mb-3">Resumen</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Campos visibles</span>
                <span className="font-medium text-green-600">{visibleCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Campos ocultos</span>
                <span className="font-medium text-slate-400">{totalCount - visibleCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Total</span>
                <span className="font-medium">{totalCount}</span>
              </div>
            </div>
          </Card>
        </div>

        {/* Contenido Principal */}
        <div className="lg:col-span-3">
          <Card className="p-6 bg-white">
            {/* Header del contenido */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{sectionInfo.icon}</span>
                <div>
                  <h2 className="text-lg font-semibold">{sectionInfo.label}</h2>
                  <p className="text-sm text-slate-500">
                    {VIEW_INFO[selectedView]?.description || 'Configuración de campos'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Buscar campo..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-48"
                  style={{ backgroundColor: '#fff' }}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowResetDialog(true)}
                >
                  <RotateCcw size={14} className="mr-1" />
                  Restablecer
                </Button>
                <button
                  onClick={handleSave}
                  disabled={!hasChanges || saving}
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium text-white disabled:opacity-50"
                  style={{ backgroundColor: hasChanges ? '#7c3aed' : '#94a3b8' }}
                >
                  <Save size={14} />
                  {saving ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </div>

            {hasChanges && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
                <AlertCircle size={16} className="text-amber-600" />
                <span className="text-sm text-amber-700">Tienes cambios sin guardar</span>
              </div>
            )}

            {/* Lista de Campos con Drag & Drop */}
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="campos">
                {(provided) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className="space-y-2"
                  >
                    {filteredCampos.map((campo, index) => (
                      <Draggable
                        key={campo.nombre}
                        draggableId={campo.nombre}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                              snapshot.isDragging 
                                ? 'bg-purple-50 border-purple-300 shadow-lg' 
                                : campo.visible 
                                  ? 'bg-white border-slate-200 hover:border-slate-300' 
                                  : 'bg-slate-50 border-slate-200 opacity-60'
                            }`}
                          >
                            {/* Drag Handle */}
                            <div
                              {...provided.dragHandleProps}
                              className="cursor-grab active:cursor-grabbing p-1 hover:bg-slate-100 rounded"
                            >
                              <GripVertical size={18} className="text-slate-400" />
                            </div>

                            {/* Campo Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-slate-800">{campo.label}</span>
                                {campo.bloqueado && (
                                  <Badge className="bg-slate-100 text-slate-600 text-xs">
                                    <Lock size={10} className="mr-1" />
                                    Bloqueado
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-slate-500 font-mono">{campo.nombre}</p>
                            </div>

                            {/* Controles */}
                            <div className="flex items-center gap-2">
                              {/* Toggle Visible */}
                              <button
                                onClick={() => handleToggleVisible(index)}
                                disabled={campo.bloqueado}
                                className={`p-2 rounded-lg transition-colors ${
                                  campo.visible 
                                    ? 'bg-green-100 text-green-600 hover:bg-green-200' 
                                    : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                                } ${campo.bloqueado ? 'cursor-not-allowed opacity-50' : ''}`}
                                title={campo.visible ? 'Ocultar campo' : 'Mostrar campo'}
                              >
                                {campo.visible ? <Eye size={18} /> : <EyeOff size={18} />}
                              </button>

                              {/* Toggle Lock */}
                              <button
                                onClick={() => handleToggleLocked(index)}
                                className={`p-2 rounded-lg transition-colors ${
                                  campo.bloqueado 
                                    ? 'bg-amber-100 text-amber-600 hover:bg-amber-200' 
                                    : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                                }`}
                                title={campo.bloqueado ? 'Desbloquear campo' : 'Bloquear campo'}
                              >
                                {campo.bloqueado ? <Lock size={18} /> : <Unlock size={18} />}
                              </button>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>

            {filteredCampos.length === 0 && (
              <div className="text-center py-8 text-slate-400">
                <Settings size={40} className="mx-auto mb-2 opacity-50" />
                <p>No hay campos que coincidan con la búsqueda</p>
              </div>
            )}

            {/* Leyenda */}
            <div className="mt-6 pt-4 border-t flex flex-wrap gap-4 text-xs text-slate-500">
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 bg-green-100 rounded flex items-center justify-center">
                  <Eye size={10} className="text-green-600" />
                </div>
                <span>Campo visible</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 bg-slate-100 rounded flex items-center justify-center">
                  <EyeOff size={10} className="text-slate-400" />
                </div>
                <span>Campo oculto</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 bg-amber-100 rounded flex items-center justify-center">
                  <Lock size={10} className="text-amber-600" />
                </div>
                <span>Campo bloqueado (siempre visible)</span>
              </div>
              <div className="flex items-center gap-1">
                <GripVertical size={14} className="text-slate-400" />
                <span>Arrastrar para reordenar</span>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Dialog: Confirmar Reset */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="text-amber-500" />
              Restablecer Configuración
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-slate-600">
              ¿Estás seguro de restablecer la configuración de campos para{' '}
              <strong>{sectionInfo.label}</strong> ({VIEW_INFO[selectedView]?.label})?
            </p>
            <p className="text-sm text-slate-500 mt-2">
              Esta acción restablecerá todos los campos a su configuración predeterminada.
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetDialog(false)}>
              Cancelar
            </Button>
            <button
              onClick={handleReset}
              className="px-4 py-2 rounded-md text-white"
              style={{ backgroundColor: '#ef4444' }}
            >
              Restablecer
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FieldConfigPage;
